import { useEffect, useState, useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api } from '../services/api';
import { Pool, PoolTeam, Pick6TeamPick, Pick6EntryPicks, PICK6_TEAM_COUNT, PICK6_ROUND_MULTIPLIERS } from '../types/pool';
import FeeNotice from '../components/FeeNotice';

export function Pick6EntryForm() {
  const { poolId, entryId } = useParams<{ poolId: string; entryId?: string }>();
  const { getAccessTokenSilently, isAuthenticated, loginWithRedirect } = useAuth0();
  const navigate = useNavigate();

  const [pool, setPool] = useState<Pool | null>(null);
  const [teams, setTeams] = useState<PoolTeam[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [entryName, setEntryName] = useState('');
  const [selectedTeams, setSelectedTeams] = useState<Pick6TeamPick[]>([]);
  const [isEditMode, setIsEditMode] = useState(false);

  // Load pool and teams
  useEffect(() => {
    if (!isAuthenticated) {
      loginWithRedirect({
        appState: { returnTo: window.location.pathname },
      });
      return;
    }

    async function loadData() {
      if (!poolId) return;

      try {
        setLoading(true);
        setError(null);

        const poolData = await api.getPool(poolId);
        setPool(poolData);
        setTeams(poolData.teams || []);

        // If editing, load existing entry by finding it in all user entries
        if (entryId && isAuthenticated) {
          setIsEditMode(true);
          const token = await getAccessTokenSilently();
          const myEntries = await api.getMyEntries(poolId, token);
          const existingEntry = myEntries.find(e => e.id === entryId);

          if (existingEntry) {
            setEntryName(existingEntry.entryName || '');
            const picks = existingEntry.picks as Pick6EntryPicks;
            if (picks?.teams) {
              setSelectedTeams(picks.teams);
            }
          }
        }
      } catch (err) {
        console.error('Failed to load data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load pool data');
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, [poolId, entryId, isAuthenticated, getAccessTokenSilently]);

  // Group teams by region
  const teamsByRegion = useMemo(() => {
    const grouped: Record<string, PoolTeam[]> = {};
    for (const team of teams) {
      const region = team.metadata?.region || 'Other';
      if (!grouped[region]) {
        grouped[region] = [];
      }
      grouped[region].push(team);
    }
    // Sort each region by seed
    for (const region of Object.keys(grouped)) {
      grouped[region].sort((a, b) => (a.seed ?? 16) - (b.seed ?? 16));
    }
    return grouped;
  }, [teams]);

  // Region colors for visual distinction
  const regionColors: Record<string, { bg: string; border: string; text: string }> = {
    'East': { bg: 'bg-blue-50', border: 'border-blue-300', text: 'text-blue-800' },
    'West': { bg: 'bg-red-50', border: 'border-red-300', text: 'text-red-800' },
    'South': { bg: 'bg-green-50', border: 'border-green-300', text: 'text-green-800' },
    'Midwest': { bg: 'bg-orange-50', border: 'border-orange-300', text: 'text-orange-800' },
    'Other': { bg: 'bg-gray-50', border: 'border-gray-300', text: 'text-gray-800' },
  };

  function isTeamSelected(teamId: string): boolean {
    return selectedTeams.some(t => t.teamId === teamId);
  }

  function toggleTeam(team: PoolTeam) {
    const teamId = team.team.id;

    if (isTeamSelected(teamId)) {
      // Remove team
      setSelectedTeams(prev => prev.filter(t => t.teamId !== teamId));
    } else {
      // Add team if under limit
      if (selectedTeams.length < PICK6_TEAM_COUNT) {
        setSelectedTeams(prev => [
          ...prev,
          {
            teamId: team.team.id,
            teamName: team.team.name,
            seed: team.seed ?? 16,
            region: team.metadata?.region,
          },
        ]);
      }
    }
  }

  // Calculate potential max points for selected teams
  const potentialPoints = useMemo(() => {
    return selectedTeams.reduce((total, team) => {
      // Max points if team wins championship
      const maxMultiplier = Object.values(PICK6_ROUND_MULTIPLIERS).reduce((a, b) => a + b, 0);
      return total + (team.seed * maxMultiplier);
    }, 0);
  }, [selectedTeams]);

  // Calculate first round points
  const firstRoundPoints = useMemo(() => {
    return selectedTeams.reduce((total, team) => total + team.seed, 0);
  }, [selectedTeams]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    // Re-check deadline at submit time (not just load time)
    if (pool && new Date(pool.deadline) < new Date()) {
      setError('The deadline for this pool has passed. You can no longer submit entries.');
      return;
    }

    if (selectedTeams.length !== PICK6_TEAM_COUNT) {
      setError(`Please select exactly ${PICK6_TEAM_COUNT} teams`);
      return;
    }

    const hasEntryFee = pool?.entryFee && Number(pool.entryFee) > 0;

    try {
      setSubmitting(true);
      setError(null);
      const token = await getAccessTokenSilently();

      const picks: Pick6EntryPicks = {
        type: 'BRACKET_PREDICTION',
        teams: selectedTeams,
      };

      const entryData: any = { entryName, picks };

      if (isEditMode && entryId) {
        await api.updateEntry(poolId!, entryId, entryData, token);
        navigate(`/pools/${poolId}`);
      } else {
        await api.createEntry(poolId!, entryData, token);
        if (hasEntryFee) {
          // Payment v2: entry is saved — take them to Settle Up to pay
          navigate('/settle-up', {
            state: { justSubmitted: true, submittedPool: pool?.name },
          });
        } else {
          navigate(`/pools/${poolId}`);
        }
      }
    } catch (err: any) {
      console.error('Failed to submit entry:', err);
      let errorMessage: string;
      if (err?.message?.includes('login_required') || err?.message?.includes('Missing Refresh Token') || err?.error === 'login_required' || err?.message?.includes('Login required')) {
        errorMessage = 'Your session has expired. Please log in again to submit your entry.';
        // Redirect to login after a brief delay so user sees the message
        setTimeout(() => {
          loginWithRedirect({ appState: { returnTo: window.location.pathname } });
        }, 2000);
      } else {
        errorMessage = err instanceof Error ? err.message : 'Failed to submit entry';
      }
      setError(errorMessage);
      // Scroll error into view
      setTimeout(() => {
        document.querySelector('[role="alert"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error && !pool) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
          <p className="font-medium">Error</p>
          <p className="text-sm mt-1">{error}</p>
          <Link to="/pools" className="mt-3 inline-block text-sm text-blue-600 hover:underline">
            ← Back to Pools
          </Link>
        </div>
      </div>
    );
  }

  if (!pool) {
    return null;
  }

  const isDeadlinePassed = new Date(pool.deadline) < new Date();

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8 max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <Link to={`/pools/${poolId}`} className="text-blue-600 hover:underline text-sm">
          ← Back to {pool.name}
        </Link>
        <h1 className="text-3xl font-bold text-gray-900 mt-2">
          {isEditMode ? 'Edit Your Pick 6' : 'Create Your Pick 6'}
        </h1>
        <p className="text-gray-600 mt-1">
          Select exactly 6 teams to score points as they advance through the tournament
        </p>
      </div>

      {/* Deadline Warning */}
      {isDeadlinePassed && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
          <p className="font-medium">⚠️ Entry Deadline Passed</p>
          <p className="text-sm">The deadline for this pool was {new Date(pool.deadline).toLocaleString()}. You cannot submit or edit entries.</p>
        </div>
      )}

      {/* Scoring Info */}
      <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-semibold text-blue-900 mb-2">📊 Scoring: Seed × Round Multiplier</h3>
        <div className="grid grid-cols-3 md:grid-cols-6 gap-2 text-sm">
          {Object.entries(PICK6_ROUND_MULTIPLIERS).map(([round, multiplier]) => (
            <div key={round} className="text-center">
              <div className="font-medium text-blue-800">×{multiplier}</div>
              <div className="text-blue-600 text-xs">{round.replace('Round of ', 'R')}</div>
            </div>
          ))}
        </div>
        <p className="text-sm text-blue-700 mt-2">
          Example: A 12-seed winning Round of 64 = 12 × 1 = 12 points
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Entry Name */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Entry Name (optional)
          </label>
          <input
            type="text"
            value={entryName}
            onChange={(e) => setEntryName(e.target.value)}
            placeholder="My Cinderella Picks"
            className="w-full max-w-md px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={isDeadlinePassed}
          />
        </div>

        {/* Selection Counter */}
        <div className="mb-4 flex items-center justify-between">
          <div className="text-lg font-semibold">
            Selected: <span className={selectedTeams.length === PICK6_TEAM_COUNT ? 'text-green-600' : 'text-blue-600'}>
              {selectedTeams.length}/{PICK6_TEAM_COUNT}
            </span>
          </div>
          <div className="text-sm text-gray-600">
            First Round Points: <span className="font-medium">{firstRoundPoints}</span>
            {' • '}
            Max Potential: <span className="font-medium">{potentialPoints}</span>
          </div>
        </div>

        {/* Selected Teams Summary */}
        {selectedTeams.length > 0 && (
          <div className="mb-6 bg-white border border-gray-200 rounded-lg p-4">
            <h3 className="font-semibold text-gray-900 mb-3">Your Picks</h3>
            <div className="flex flex-wrap gap-2">
              {selectedTeams.map((team) => {
                const colors = regionColors[team.region || 'Other'];
                return (
                  <div
                    key={team.teamId}
                    className={`px-3 py-2 rounded-lg ${colors.bg} border ${colors.border} flex items-center gap-2`}
                  >
                    <span className="font-medium text-gray-500">({team.seed})</span>
                    <span className={`font-medium ${colors.text}`}>{team.teamName}</span>
                    <button
                      type="button"
                      onClick={() => setSelectedTeams(prev => prev.filter(t => t.teamId !== team.teamId))}
                      className="ml-1 text-gray-400 hover:text-red-500"
                      disabled={isDeadlinePassed}
                    >
                      ✕
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Team Selection by Region */}
        {teams.length === 0 ? (
          <div className="mb-8 bg-amber-50 border-2 border-amber-300 rounded-lg p-6 text-center">
            <span className="text-3xl block mb-2">🏀</span>
            <h3 className="font-semibold text-gray-900 mb-2">Bracket Not Yet Available</h3>
            <p className="text-gray-600">Tournament teams will be available once the bracket is imported. Check back after Selection Sunday!</p>
          </div>
        ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {['East', 'West', 'South', 'Midwest'].map((region) => {
            const regionTeams = teamsByRegion[region] || [];
            const colors = regionColors[region];

            return (
              <div key={region} className={`rounded-lg border-2 ${colors.border} overflow-hidden`}>
                <div className={`px-4 py-2 ${colors.bg}`}>
                  <h3 className={`font-bold ${colors.text}`}>{region} Region</h3>
                </div>
                <div className="p-3 bg-white">
                  <div className="grid grid-cols-2 gap-2">
                    {regionTeams.map((team) => {
                      const isSelected = isTeamSelected(team.team.id);
                      const isDisabled = isDeadlinePassed || (!isSelected && selectedTeams.length >= PICK6_TEAM_COUNT);

                      return (
                        <button
                          key={team.team.id}
                          type="button"
                          onClick={() => toggleTeam(team)}
                          disabled={isDisabled}
                          className={`px-3 py-2 rounded text-sm font-medium transition-all text-left flex items-center gap-2 ${
                            isSelected
                              ? `${colors.bg} ${colors.border} border-2 ${colors.text}`
                              : isDisabled
                              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                              : 'bg-gray-50 hover:bg-gray-100 text-gray-700 border border-gray-200'
                          }`}
                        >
                          <span className="text-gray-500 w-6">({team.seed})</span>
                          <span className="truncate">{team.team.shortName || team.team.name}</span>
                          {isSelected && <span className="ml-auto">✓</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 text-red-800">
            {error}
          </div>
        )}

        {/* Entry fee notice — payment happens after submit (Settle Up) */}
        {pool && pool.entryFee && Number(pool.entryFee) > 0 && !isEditMode && (
          <div className="mb-6">
            <FeeNotice entryFee={Number(pool.entryFee)} />
          </div>
        )}

        {/* Error near submit button so users see it */}
        {error && (
          <div className="mb-4 bg-red-50 border-2 border-red-300 rounded-lg p-4 text-red-800 font-medium" role="alert">
            {error}
          </div>
        )}

        {/* Submit Button */}
        <div className="flex gap-4">
          <button
            type="submit"
            disabled={submitting || isDeadlinePassed || selectedTeams.length !== PICK6_TEAM_COUNT}
            className="px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {submitting && (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            )}
            {submitting ? 'Submitting...' : isEditMode ? 'Update Entry' : 'Submit Entry'}
          </button>
          <Link
            to={`/pools/${poolId}`}
            className="px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors"
          >
            Cancel
          </Link>
        </div>
      </form>

      {/* Strategy Tips */}
      <div className="mt-8 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <h3 className="font-semibold text-yellow-800 mb-2">💡 Strategy Tips</h3>
        <ul className="text-sm text-yellow-800 space-y-1">
          <li>• <strong>High seeds (1-4)</strong> are safer but score fewer points per win</li>
          <li>• <strong>Mid seeds (5-9)</strong> offer a balance of risk and reward</li>
          <li>• <strong>Low seeds (10-16)</strong> are risky but score big if they advance</li>
          <li>• A 12-seed upset in Round 1 scores as much as a 1-seed winning 3 games!</li>
        </ul>
      </div>
    </div>
  );
}
